import MainBlock from "components/DesignSystem/LayoutBlocks/MainBlock";
import React, {
    useEffect,
    useContext,
    Fragment,
    useRef,
    useReducer,
} from "react";
import { useAuth } from "hooks/Auth";
import { useParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import {
    apiDel,
    apiGet,
    apiPost,
    apiPut,
    messageFromException,
} from "helpers/Request";
import {
    transform as issueTransformer,
    IssueObjectSchema,
} from "schemas/Issue";
import { useHistory } from "react-router-dom";
import { toast } from "react-hot-toast";
import { AppContext } from "context/App";
import { Formik, Form } from "formik";
import TextInput from "components/FormElements/TextInput";
import Toolbar, { ToolbarGroup } from "components/Toolbar/Toolbar";
import ToolbarButton from "components/Toolbar/ToolbarButton";
import { SaveIcon, TrashIcon, SupportIcon } from "@heroicons/react/outline";
import { useContextualHelp } from "hooks/Help";
import CheckBox from "components/FormElements/CheckBox";
import DateInput from "components/FormElements/DateInput";
import RichEditor from "components/FormElements/RichEditor";
import {
    fromString,
    fromLocalString,
    HTML_DATE_FORMAT,
    SQL_DATE_TIME_FORMAT,
} from "helpers/Date";
import { modal } from "components/HeadlessModal";
import DangerButton from "components/Buttons/DangerButton";
import SecondaryButton from "components/Buttons/SecondaryButton";

const reducer = (oldState, newState) => {
    return Object.assign({}, oldState, newState);
};

export default function IssueEditScreen(props) {
    const { uuid } = useParams();
    const formRef = useRef();
    const location = useLocation();
    const auth = useAuth();
    const history = useHistory();
    const contextualHelp = useContextualHelp();

    const [state, setState] = useReducer(reducer, {
        issue: IssueObjectSchema,
        formValues: IssueObjectSchema,
        loading: false,
        saving: false,
    });

    const { setCurrentNav } = useContext(AppContext);

    const onSubmit = (values) => {
        const data = Object.assign({}, state.issue, values, {
            published_at: values.published_at
                ? fromLocalString(
                      values.published_at.toString(),
                      HTML_DATE_FORMAT
                  )
                      .toUTC()
                      .toFormat(SQL_DATE_TIME_FORMAT)
                : null,
        });

        setState({ saving: true });

        const apiAction = state.issue.uuid ? apiPut : apiPost;
        apiAction(
            "/v4/issue/" + (state.issue.uuid ? state.issue.uuid : ""),
            auth.publication,
            data
        )
            .then((resp) => {
                const item = issueTransformer(resp);
                toast.success("Issue saved");
                if (!state.issue.uuid) {
                    // was a new item, so redirect
                    history.push("/app/planner/issue/" + item.uuid);
                }
                setState({ issue: item, saving: false, loading: false });
            })
            .catch((err) => {
                setState({ saving: false });
                toast.error("Unable to save your changes, please try again.");
            });
    };

    const onDelete = () => {
        modal.warning({
            title: "Delete Issue",
            confirmText: "Yes, delete issue",
            children: (
                <div>
                    <div className="mb-4">
                        <strong>
                            Are you sure you want to delete this issue?
                        </strong>
                    </div>
                    <div>
                        <strong>Note:</strong> This action cannot be undone.
                    </div>
                </div>
            ),
            buttons: () => (
                <Fragment>
                    <DangerButton onClick={deleteIssue}>Delete</DangerButton>
                    <SecondaryButton onClick={modal.dismiss}>
                        Cancel
                    </SecondaryButton>
                </Fragment>
            ),
        });
    };

    const deleteIssue = async () => {
        const id = toast.loading("Deleting issue...");
        return apiDel(`/v4/issue/${state.issue.uuid}`, auth.publication)
            .then((resp) => {
                toast.success("Issue deleted", { id });
                modal.dismiss();
                history.push("/app/planner/issue");
            })
            .catch((err) => {
                modal.dismiss();
                toast.dismiss(id);
                modal.error({
                    title: "Unable to delete issue",
                    children: messageFromException(err),
                });
            });
    };

    useEffect(() => {
        setState({ loading: true, issue: IssueObjectSchema });

        if (!uuid || uuid === "new") {
            setTimeout(() => {
                setState({ loading: false });
            }, 0);
            return;
        }

        apiGet("/v4/issue/" + uuid, auth.publication, { include: "content" })
            .then((response) => {
                const issue = issueTransformer(response);
                const item = Object.assign({}, issueTransformer(response), {
                    published_at: issue.published_at
                        ? fromString(issue.published_at)
                              .toLocal()
                              .toFormat(HTML_DATE_FORMAT)
                        : null,
                });

                setState({ issue: item, loading: false });
            })
            .catch((err) => {
                console.error(err);
                setState({ loading: false });
                modal.warning({
                    title: "Unable to load issue",
                    children: messageFromException(err),
                });
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, uuid, auth.publication]);

    useEffect(() => {
        setCurrentNav("planner");
    }, [setCurrentNav]);

    useEffect(() => {
        // fixes a bug where the console notes a component updating another, unmounted
        // component. just delay the update until the next tick
        setTimeout(() => {
            setState({ formValues: Object.assign({}, state.issue) });
        });
    }, [state.issue]);

    // simply watches the loading indicator and resets the form when it changes.
    // this should always be the last `useEffect` in the sequence.
    useEffect(() => {
        setTimeout(() => {
            formRef.current.resetForm({
                values: state.formValues,
            });
        });
    }, [state.loading, state.formValues]);

    return (
        <MainBlock className="z-0">
            <Formik
                innerRef={formRef}
                initialValues={state.formValues}
                onSubmit={onSubmit}
                enableReinitialize={false}
            >
                {({ setFieldValue }) => (
                    <Form>
                        <div className="mx-auto max-w-2xl space-y-4">
                            <Toolbar>
                                <ToolbarGroup>
                                    <ToolbarButton
                                        icon={<SaveIcon />}
                                        type="submit"
                                        disabled={state.saving}
                                        blocking={state.saving}
                                    >
                                        Save
                                    </ToolbarButton>
                                    <ToolbarButton
                                        icon={<TrashIcon />}
                                        onClick={onDelete}
                                        disabled={
                                            state.saving || !state.issue.uuid
                                        }
                                    >
                                        Delete
                                    </ToolbarButton>
                                </ToolbarGroup>
                                <ToolbarGroup className="ml-auto">
                                    <ToolbarButton
                                        icon={<SupportIcon />}
                                        className="text-green-600"
                                        onClick={() => {
                                            contextualHelp.showContextualHelp(
                                                "/planner/issues.html"
                                            );
                                        }}
                                    >
                                        Help
                                    </ToolbarButton>
                                </ToolbarGroup>
                            </Toolbar>
                            {state.loading ? (
                                <Fragment>
                                    <div className="h-10 w-full animate-pulse bg-gray-200"></div>
                                    <div className="h-10 w-full animate-pulse bg-gray-200"></div>
                                    <div className="h-10 w-full animate-pulse bg-gray-200"></div>
                                </Fragment>
                            ) : (
                                <div className="space-y-4 px-2 sm:px-0">
                                    <TextInput
                                        label="Label"
                                        name="label"
                                        disabled={state.saving}
                                    />

                                    <CheckBox
                                        name="status"
                                        label="Status"
                                        disabled={state.saving}
                                        helpertext="This issue is active."
                                    />

                                    <DateInput
                                        name="published_at"
                                        label="Issue Date"
                                        disabled={state.saving}
                                        helpertext="When this issue will be published."
                                    />

                                    <RichEditor
                                        name="description"
                                        label="Description"
                                        disabled={state.saving}
                                    />
                                </div>
                            )}
                        </div>
                    </Form>
                )}
            </Formik>
        </MainBlock>
    );
}
